import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num } from '../lib/format.ts';

interface Buckets {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
  total: number;
}

interface Row {
  customerNo: string;
  customerName: string | null;
  invoiceCount: number;
  buckets: Buckets;
}

interface AgingResponse {
  asOf: string;
  totals: Buckets;
  customerCount: number;
  rows: Row[];
}

interface InvoiceRow {
  invoiceNo: string;
  invoiceDate: string;
  dueDate: string | null;
  daysOverdue: number;
  bucket: 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';
  value: number;
  outstanding: number;
  status: string;
}

function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

export function ArAgingPage() {
  const [asOf, setAsOf] = useState<string>(todayYmd());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['ar-aging', asOf],
    queryFn: () => apiFetch<AgingResponse>(`/api/dashboard/aging/ar?asOf=${asOf}`),
  });

  const filtered = data
    ? data.rows.filter(
        (r) =>
          !search ||
          r.customerNo.toLowerCase().includes(search.toLowerCase()) ||
          (r.customerName ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">AR Aging</h1>
        <span className="main-subtitle">
          {data ? `As of ${dateOnly(data.asOf)} · ${data.customerCount} customers with outstanding` : ''}
        </span>
      </header>

      <section className="panel" style={{ padding: '0.75rem 1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
          <label style={labelStyle}>As of</label>
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            style={{ padding: '0.3rem' }}
          />
          <span style={{ width: 8 }} />
          <input
            placeholder="Filter customer name or code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200 }}
          />
        </div>
      </section>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <div className="kpi-grid">
            <BucketKPI label="Current" value={data.totals.current} note="not yet due" />
            <BucketKPI label="1-30 days" value={data.totals.d1_30} note="overdue" />
            <BucketKPI label="31-60 days" value={data.totals.d31_60} note="overdue" />
            <BucketKPI label="61-90 days" value={data.totals.d61_90} note="overdue" />
            <BucketKPI label="90+ days" value={data.totals.d90_plus} note="overdue" flag={data.totals.d90_plus > 0} />
            <BucketKPI label="Total AR" value={data.totals.total} highlight />
          </div>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">By customer</h2>
              <span className="panel-subtitle">{filtered.length} shown · click to expand</span>
            </header>
            {filtered.length === 0 ? (
              <div className="empty">No customers with outstanding invoices match.</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th className="num">Invoices</th>
                    <th className="num">Current</th>
                    <th className="num">1-30</th>
                    <th className="num">31-60</th>
                    <th className="num">61-90</th>
                    <th className="num">90+</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <CustomerRow
                      key={r.customerNo}
                      row={r}
                      asOf={asOf}
                      expanded={expanded === r.customerNo}
                      onToggle={() =>
                        setExpanded(expanded === r.customerNo ? null : r.customerNo)
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </>
  );
}

function CustomerRow({
  row,
  asOf,
  expanded,
  onToggle,
}: {
  row: Row;
  asOf: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['ar-aging', row.customerNo, asOf],
    queryFn: () =>
      apiFetch<{ rows: InvoiceRow[]; asOf: string }>(
        `/api/dashboard/aging/ar/${encodeURIComponent(row.customerNo)}?asOf=${asOf}`,
      ),
    enabled: expanded,
  });

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td>
          <Link
            to={`/customers/${encodeURIComponent(row.customerNo)}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.customerName ?? row.customerNo}
          </Link>
          <div className="mono" style={{ color: 'var(--text-dim)', fontSize: 11 }}>
            {row.customerNo}
          </div>
        </td>
        <td className="num">{num(row.invoiceCount)}</td>
        <td className="num">{cell(row.buckets.current)}</td>
        <td className="num">{cell(row.buckets.d1_30)}</td>
        <td className="num" style={{ color: row.buckets.d31_60 > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
          {cell(row.buckets.d31_60)}
        </td>
        <td className="num" style={{ color: row.buckets.d61_90 > 0 ? 'var(--text)' : 'var(--text-dim)' }}>
          {cell(row.buckets.d61_90)}
        </td>
        <td className="num" style={{ color: row.buckets.d90_plus > 0 ? 'var(--danger, #ff6b6b)' : 'var(--text-dim)', fontWeight: row.buckets.d90_plus > 0 ? 600 : 400 }}>
          {cell(row.buckets.d90_plus)}
        </td>
        <td className="num" style={{ fontWeight: 600 }}>{money(row.buckets.total)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--surface-2)', padding: '0.5rem 1rem' }}>
            {isLoading && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading invoices…</div>}
            {data && (
              <table className="table" style={{ background: 'transparent' }}>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th className="num">Days overdue</th>
                    <th className="num">Value</th>
                    <th className="num">Outstanding</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((i) => (
                    <tr key={i.invoiceNo}>
                      <td className="mono">
                        <Link to={`/invoices/${encodeURIComponent(i.invoiceNo)}`}>{i.invoiceNo}</Link>
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>{dateOnly(i.invoiceDate)}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {i.dueDate ? dateOnly(i.dueDate) : '—'}
                      </td>
                      <td className="num">{i.daysOverdue > 0 ? num(i.daysOverdue) : '—'}</td>
                      <td className="num">{money(i.value)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{money(i.outstanding)}</td>
                      <td>
                        <span className="badge badge-neutral">{i.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function cell(v: number): string {
  return v === 0 ? '—' : money(v);
}

function BucketKPI({
  label,
  value,
  note,
  flag,
  highlight,
}: {
  label: string;
  value: number;
  note?: string;
  flag?: boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className="kpi-card"
      style={{
        borderColor: flag ? 'var(--danger, #ff6b6b)' : highlight ? 'var(--accent)' : undefined,
      }}
    >
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{money(value)}</div>
      {note && <div className="kpi-sub">{note}</div>}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};
